import { useState } from "react"

import { EnkInputField } from "../inputs/inputfield"
import { EnkInput } from "../inputs/input"
import { EnkFieldGroup } from "../inputs/field-group"
import { EnkButtonBar, EnkPrimaryButton } from "../common/buttons"
import { EnkCheckbox } from "../inputs/checkbox"
import { EnkCombobox } from "../inputs/combobox"

interface IEnkReportParamsProps {
  params: string[]
  onCommit: (params: string[]) => void
}

type ParamType =
  | "boolean"
  | "bevel"
  | "string"
  | "memo"
  | "date"
  | "time"
  | "integer"
  | "float"
  | "combobox"
  | "filename"
  | "directory"
  | "label"
  | "lookupfield"

function strToParamType(value: string): ParamType {
  switch (value.toLowerCase()) {
    case "boolean":
      return "boolean"
    case "bevel":
      return "bevel" // Assuming bevel is treated as string
    case "string":
      return "string"
    case "memo":
      return "memo"
    case "datetime":
    case "tdatetime":
    case "date":
      return "date"
    case "time":
    case "ttime":
      return "time"
    case "integer":
      return "integer"
    case "float":
      return "float"
    case "cblist":
      return "combobox"
    case "filename":
      return "filename"
    case "directory":
      return "directory"
    case "label":
      return "label"
    default:
      return "lookupfield"
  }
}

function parseCommaText(input: string): string[] {
  const result: string[] = []
  let current = ""
  let inQuotes = false
  let i = 0

  while (i < input.length) {
    const char = input[i]

    if (char === '"') {
      inQuotes = !inQuotes
    } else if (char === "," && !inQuotes) {
      result.push(current.trim())
      current = ""
    } else {
      current += char
    }

    i++
  }

  if (current) {
    result.push(current.trim())
  }

  return result
}

function strToParamInfo(value: string): { paramType: ParamType; defaultValue: string } {
  let datatype = value.trim()
  let defaultValue = ""

  if (datatype.includes(";")) {
    defaultValue = datatype.substring(datatype.indexOf(";") + 1).trim()
    datatype = datatype.substring(0, datatype.indexOf(";")).trim()
  }

  return { paramType: strToParamType(datatype), defaultValue }
}

export default function EnkReportParams(props: IEnkReportParamsProps) {
  const { onCommit } = props

  const [editValues, setEditValues] = useState<{ [key: string]: string }>({})

  const handleCommit = () => {
    console.log("commiting", editValues)
    const reportParams = props.params.map((param: string) => {
      const [name] = param.split("=")
      return `${name}=${editValues[name] || ""}`
    })
    console.log("reportParams", reportParams)
    onCommit(reportParams)
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { id, value, type } = event.target
    setEditValues((prevParams) => {
      const newParams = { ...prevParams }
      const checked = event.target.type === "checkbox" ? event.target.checked : false
      const newValue = type === "checkbox" ? (checked ? "true" : "false") : value
      newParams[id] = newValue
      return newParams
    })
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        handleCommit()
      }}
    >
      {/* Report parameters */}
      <EnkFieldGroup label=" " description="Vul de parameters in die je wil gebruiken voor het rapport.">
        {props.params.map((param, index) => {
          const [name, value] = param.split("=")
          const { paramType, defaultValue } = strToParamInfo(value)

          switch (paramType) {
            case "boolean":
              return (
                <div key={index} className="col-span-full col-start-1">
                  <EnkCheckbox id={name} label={name} checked={editValues[name] == "1"} onChange={handleInputChange} />
                </div>
              )
            case "string":
              return (
                <EnkInputField key={index} label={name} className="col-span-4 col-start-1">
                  <EnkInput id={name} type="text" placeholder={defaultValue} value={editValues[name] || ""} onChange={handleInputChange} />
                </EnkInputField>
              )
            case "float":
            case "integer":
              return (
                <EnkInputField key={index} label={name} className="col-span-1 col-start-1">
                  <EnkInput id={name} type="number" placeholder={defaultValue} value={editValues[name] || ""} onChange={handleInputChange} />
                </EnkInputField>
              )
            case "date":
              return (
                <EnkInputField key={index} label={name} className="col-span-2 col-start-1">
                  <EnkInput id={name} type="date" placeholder={value} value={editValues[name] || ""} onChange={handleInputChange} />
                </EnkInputField>
              )
            case "combobox": {
              let options: string[] = []
              let itemIndex = -1
              if (defaultValue.includes(";")) {
                options = parseCommaText(defaultValue.split(";")[1])
                const parsedIndex = parseInt(defaultValue.split(";")[0])
                itemIndex = isNaN(parsedIndex) ? -1 : parsedIndex
              } else {
                options = parseCommaText(defaultValue)
              }
              return (
                <EnkInputField key={index} label={name} className="col-span-full col-start-1">
                  <EnkCombobox id={name} value={itemIndex} options={options} />
                </EnkInputField>
              )
            }
            default:
              return (
                <EnkInputField key={index} label={name} className="col-span-4 col-start-1">
                  <EnkInput id={name} type="text" placeholder={value} value={editValues[name] || ""} onChange={handleInputChange} />
                </EnkInputField>
              )
          }
        })}
      </EnkFieldGroup>
      {/* Submit button */}
      <EnkButtonBar>
        <EnkPrimaryButton text="Toon rapport" onClick={() => void handleCommit()} />
      </EnkButtonBar>
    </form>
  )
}
